import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Trash2, UserPlus, Shield } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { glassCardStatic, glassCardLight } from '@/utils/admin-glass-styles';

interface Admin {
  user_id: string;
  email?: string;
  created_at: string;
}

const AdminManagement: React.FC = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingAdmin, setIsAddingAdmin] = useState(false);
  const [addAdminDebug, setAddAdminDebug] = useState<{ type: 'error' | 'success'; message: string } | null>(null);
  const { toast } = useToast();

  const getFunctionErrorMessage = async (error: any): Promise<string> => {
    const fallbackMessage = error?.message || 'Failed to process admin request';

    try {
      const context = error?.context;
      if (context) {
        if (typeof context === 'object' && context !== null && typeof context.clone !== 'function') {
          const msg = context.error || context.details || context.message;
          if (msg && typeof msg === 'string') return msg;
        }

        if (typeof context.clone === 'function') {
          try {
            const rawText = await context.clone().text();
            if (rawText) {
              try {
                const payload = JSON.parse(rawText);
                return payload?.error || payload?.details || rawText;
              } catch {
                return rawText;
              }
            }
          } catch {
            // clone/text failed
          }
        }
      }

      if (fallbackMessage && fallbackMessage !== 'Edge Function returned a non-2xx status code') {
        try {
          const parsed = JSON.parse(fallbackMessage);
          return parsed?.error || parsed?.details || fallbackMessage;
        } catch {
          // not JSON, use as-is
        }
      }
    } catch (parseError) {
      console.error('Failed to parse function error payload:', parseError);
    }

    return fallbackMessage;
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-admin-users', {
        body: { action: 'list_admins' },
      });

      if (error) throw error;

      setAdmins(data?.admins || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast({
        title: "Error",
        description: "Failed to fetch admin list",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addAdmin = async () => {
    if (!newAdminEmail.trim()) {
      toast({
        title: "Error",
        description: "Please enter an email address",
        variant: "destructive",
      });
      return;
    }

    const normalizedEmail = newAdminEmail.trim().toLowerCase();
    setAddAdminDebug(null);

    setIsAddingAdmin(true);
    try {
      const { data: addAdminData, error: addAdminError } = await supabase.functions.invoke('get-admin-users', {
        body: {
          action: 'add_admin',
          email: normalizedEmail,
        },
      });
      
      if (addAdminError) {
        let detailedMessage: string;
        try {
          detailedMessage = await getFunctionErrorMessage(addAdminError);
        } catch {
          detailedMessage = addAdminError.message || 'Failed to add admin';
        }
        console.error('Add admin error:', { error: addAdminError, extractedMessage: detailedMessage });
        throw new Error(detailedMessage);
      }

      if (!addAdminData?.success) {
        const serverError = addAdminData?.error || addAdminData?.details || 'Failed to add admin';
        console.error('Add admin response (no success):', addAdminData);
        throw new Error(serverError);
      }

      setAddAdminDebug({ type: 'success', message: `${normalizedEmail} has been added as admin.` });
      toast({
        title: "Success",
        description: `${normalizedEmail} has been added as an admin`,
      });

      setNewAdminEmail('');
      fetchAdmins();
    } catch (error) {
      console.error('Error adding admin:', error);
      const fallbackText =
        typeof error === 'string'
          ? error
          : (error && typeof error === 'object' && 'message' in error
              ? String((error as { message?: unknown }).message || '')
              : '');
      const errorText = error instanceof Error
        ? error.message
        : (fallbackText || "Failed to add admin");
      setAddAdminDebug({ type: 'error', message: errorText });
      toast({
        title: "Error",
        description: errorText,
        variant: "destructive",
      });
    } finally {
      setIsAddingAdmin(false);
    }
  };

  const removeAdmin = async (userId: string, email: string) => {
    try {
      const { error } = await supabase
        .from('admins')
        .delete()
        .eq('user_id', userId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `${email} has been removed from admin role`,
      });

      fetchAdmins();
    } catch (error) {
      console.error('Error removing admin:', error);
      toast({
        title: "Error",
        description: "Failed to remove admin",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Admin Management</h1>
        <p className="text-muted-foreground">Manage admin users and permissions</p>
      </div>

      {/* Add New Admin */}
      <Card className={glassCardStatic}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add New Admin
          </CardTitle>
          <CardDescription>
            Please add a new admin user by email address. The user must already have an account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1">
              <Label htmlFor="adminEmail">Email Address</Label>
              <Input
                id="adminEmail"
                type="email"
                placeholder="Enter user email..."
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                className="mt-1"
              />
            </div>
            <Button 
              onClick={addAdmin} 
              disabled={isAddingAdmin}
              className="sm:mt-6"
            >
              {isAddingAdmin ? 'Adding...' : 'Add Admin'}
            </Button>
          </div>
          {addAdminDebug && (
            <div
              className={`text-xs rounded-md p-2 break-all ${
                addAdminDebug.type === 'error'
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-emerald-100 text-emerald-800'
              }`}
            >
              {addAdminDebug.message}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Current Admins */}
      <Card className={glassCardStatic}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Current Admins
          </CardTitle>
          <CardDescription>
            All users with admin privileges
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-4">Loading admins...</div>
          ) : admins.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">No admins found</div>
          ) : (
            <div className="space-y-2">
              {admins.map((admin) => (
                <div key={admin.user_id} className={`flex items-center justify-between p-3 rounded-lg ${glassCardLight}`}>
                  <div>
                    <div className="font-medium">{admin.email}</div>
                    <div className="text-sm text-muted-foreground">
                      Added: {new Date(admin.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove Admin</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to remove {admin.email} from admin role? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => removeAdmin(admin.user_id, admin.email)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Remove Admin
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminManagement;
